import { useState, type FC } from 'react'

import { Checkbox, StyledText } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import injuryreportResources from '@/api/injuryReports'
import queries from '@/api/queries'

import { FormattedNumberWithCurrency } from '../FormattedNumberWithCurrency'
import { Translation } from '../i18n/Translation'
import { LocalDate } from '../LocalDate'

import type { BuyoutOffering } from './types'

type BuyOutOfferingContentProps = {
  data: BuyoutOffering
}

export const BuyOutOfferingContent: FC<BuyOutOfferingContentProps> = ({ data }) => {
  const [isTermsAccepted, setIsTermsAccepted] = useState(false)
  const queryClient = useQueryClient()

  const acceptBuyoutOfferMutation = useMutation(
    (id: number) => injuryreportResources.acceptBuyoutOffer(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queries.injuryReportAssets())
      },
    }
  )

  const declineBuyoutOfferMutation = useMutation(
    (id: number) => injuryreportResources.declineBuyoutOffer(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queries.injuryReportAssets())
      },
    }
  )

  const onAcceptBuyoutOffer = () => {
    if (!isTermsAccepted) return
    acceptBuyoutOfferMutation.mutate(data.id)
  }
  const onDeclineBuyoutOffer = () => {
    declineBuyoutOfferMutation.mutate(data.id)
  }

  const isRejectedOrExpired = data.status === 'REJECTED' || data.status === 'EXPIRED'
  const isPending = data.status === 'PENDING'
  const isAccepted = data.status === 'ACCEPTED'
  const isInactive = isRejectedOrExpired || isAccepted

  return (
    <>
      <h4 className="mb-2 text-sm font-medium text-gray-700">
        <Translation id="Buyout Offering:" />
      </h4>
      <div className="space-y-2 pl-0 sm:pl-6 md:pl-10">
        <div className="flex flex-col justify-between md:flex-row">
          <span className="text-sm text-gray-600">
            <Translation
              id="You will be charged {price} through payroll deduction."
              price={
                <StyledText fontWeight="bold">
                  <FormattedNumberWithCurrency amount={data.buyoutPrice} currency={data.currency} />
                </StyledText>
              }
            />
          </span>
          <span className="text-sm text-gray-600">
            <Translation
              id="Offer Expiration Date: {date}"
              date={
                <StyledText fontWeight="bold">
                  <LocalDate date={data.offerExpirationDate} />
                </StyledText>
              }
            />
          </span>
        </div>
        <Checkbox
          id={`buyout-terms-checkbox-${data.id}`}
          checked={isTermsAccepted || isAccepted}
          onChange={() => setIsTermsAccepted(!isTermsAccepted)}
          isDisabled={isInactive}
          label={
            <Translation id="Arbeidstaker gir ved denne aksept arbeidsgiver fullmakt til å foreta lønntrekk for kostnader tilknyttet denne bestillingen. Jf. Arbeidsmiljøloven § 14-15 annet ledd bokstav c." />
          }
        />
      </div>
      {/* 2 buttons container */}
      {isPending && (
        <div className="mt-4 flex flex-col space-x-2 md:flex-row">
          <Button
            className="flex-1 rounded-lg bg-gray-100 px-6 py-2.5 font-medium text-gray-700 transition-colors hover:bg-gray-200"
            onClick={onAcceptBuyoutOffer}
            disabled={!isTermsAccepted || isInactive}
          >
            <Translation id="Accept offer" />
          </Button>
          <Button
            className="flex-1 rounded-lg bg-gray-100 px-6 py-2.5 font-medium text-gray-700 transition-colors hover:bg-gray-200"
            onClick={onDeclineBuyoutOffer}
            disabled={isInactive}
          >
            <Translation id="Decline offer" />
          </Button>
        </div>
      )}
    </>
  )
}
